import { useState } from 'react';

export default function Booking() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    experience: '',
    participants: '1',
    weight: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.phone || !formData.date || !formData.experience || !formData.weight) {
      alert('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4jb0l2eh8g2mrj4avog', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          date: '',
          experience: '',
          participants: '1',
          weight: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailBooking = () => {
    const subject = encodeURIComponent('Paragliding Booking Request');
    const body = encodeURIComponent(
      `Name: ${formData.name}\n` +
      `Email: ${formData.email}\n` +
      `Phone: ${formData.phone}\n` +
      `Preferred Date: ${formData.date}\n` +
      `Flight Experience: ${formData.experience}\n` +
      `Participants: ${formData.participants}\n` +
      `Weight: ${formData.weight} kg\n` +
      `Additional Information: ${formData.message}`
    );
    window.location.href = `mailto:darioparapentetandem@gmail.com?subject=${subject}&body=${body}`;
  };

  return (
    <section id="booking" className="py-24 bg-gradient-to-b from-white to-sky-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Book Your Fly
          </h2>
          <p className="text-xl text-gray-600">
            Contact <a href="mailto:darioparapentetandem@gmail.com" className="text-sky-600 hover:text-sky-700 font-semibold underline cursor-pointer">darioparapentetandem@gmail.com</a> to reserve your paragliding adventure
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <form id="booking-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-600 focus:border-transparent text-sm"
                  required
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-600 focus:border-transparent text-sm"
                  required
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-600 focus:border-transparent text-sm"
                  required
                />
              </div>

              <div>
                <label htmlFor="date" className="block text-sm font-semibold text-gray-700 mb-2">
                  Preferred Date *
                </label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-600 focus:border-transparent text-sm"
                  required
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="experience" className="block text-sm font-semibold text-gray-700 mb-2">
                  Flight Experience *
                </label>
                <div className="relative">
                  <select
                    id="experience"
                    name="experience"
                    value={formData.experience}
                    onChange={(e) => setFormData({...formData, experience: e.target.value})}
                    className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-600 focus:border-transparent appearance-none text-sm cursor-pointer"
                    required
                  >
                    <option value="">Select an experience</option>
                    <option value="Discovery Flight">Discovery Flight</option>
                    <option value="Thermal Flight">Thermal Flight</option>
                    <option value="Acrobatic Flight">Acrobatic Flight</option>
                    <option value="Sunset Flight">Sunset Flight</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                </div>
              </div>

              <div>
                <label htmlFor="participants" className="block text-sm font-semibold text-gray-700 mb-2">
                  Number of Participants
                </label>
                <div className="relative">
                  <select
                    id="participants"
                    name="participants"
                    value={formData.participants}
                    onChange={(e) => setFormData({...formData, participants: e.target.value})}
                    className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-600 focus:border-transparent appearance-none text-sm cursor-pointer"
                  >
                    <option value="1">1 Person</option>
                    <option value="2">2 People</option>
                    <option value="3">3 People</option>
                    <option value="4">4 People</option>
                    <option value="5">5+ People</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="weight" className="block text-sm font-semibold text-gray-700 mb-2">
                Weight (kg) *
              </label>
              <input
                type="number"
                id="weight"
                name="weight"
                value={formData.weight}
                onChange={(e) => setFormData({...formData, weight: e.target.value})}
                placeholder="Required for safety calculations"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-600 focus:border-transparent text-sm"
                required
                min="30"
                max="120"
              />
              <p className="text-xs text-gray-500 mt-1">Weight limit: 30-120 kg</p>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                Additional Information
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setFormData({...formData, message: e.target.value});
                  }
                }}
                rows={4}
                maxLength={500}
                placeholder="Any special requests or questions?"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-600 focus:border-transparent text-sm resize-none"
              ></textarea>
              <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-sky-600 text-white py-4 rounded-full text-lg font-semibold hover:bg-sky-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Booking Request'}
              </button>

              <button
                type="button"
                onClick={handleEmailBooking}
                className="w-full bg-white text-sky-600 py-4 rounded-full text-lg font-semibold border-2 border-sky-600 hover:bg-sky-50 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-mail-line"></i>
                Send via Email
              </button>
            </div>

            {submitStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                <p className="font-semibold">Booking request submitted successfully!</p>
                <p className="text-sm">We'll contact you within 24 hours to confirm your flight.</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                <p className="font-semibold">Something went wrong.</p>
                <p className="text-sm">Please try again or contact us directly.</p>
              </div>
            )}
          </form>

          <div className="mt-8 pt-8 border-t border-gray-200">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <i className="ri-phone-fill text-3xl text-sky-600 mb-2"></i>
                <p className="font-semibold text-gray-900">Call Us</p>
                <p className="text-sm text-gray-600">0034603904525</p>
              </div>
              <div>
                <i className="ri-mail-fill text-3xl text-sky-600 mb-2"></i>
                <p className="font-semibold text-gray-900">Email Us</p>
                <p className="text-sm text-gray-600">darioparapentetandem@gmail.com</p>
              </div>
              <div>
                <i className="ri-map-pin-fill text-3xl text-sky-600 mb-2"></i>
                <p className="font-semibold text-gray-900">Location</p>
                <p className="text-sm text-gray-600">Mountain Valley, Spain</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
